import React, { useCallback, useEffect, useState } from 'react';

export interface ListboxKeyboardOptions<T> {
  items: T[];
  isOpen: boolean;
  onCommit: (item: T) => void;
  getItemId: (item: T) => string;
}
export interface ListboxKeyboard {
  activeIndex: number;
  activeDescendantId: string | undefined;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

/** Roving listbox keyboard nav for the aria-activedescendant pattern: Arrow up/down move a
 *  single active index, Enter commits the active item (or items[0]), the highlight resets on
 *  items change, and the active row is scrolled into view. `items` must be the currently
 *  VISIBLE rows in render order; `getItemId` must return a valid DOM id (caller encodes). */
export function useListboxKeyboard<T>({
  items, isOpen, onCommit, getItemId,
}: ListboxKeyboardOptions<T>): ListboxKeyboard {
  const [activeIndex, setActiveIndex] = useState(-1);

  // Reset on the id-SEQUENCE, not the array identity: callers commonly pass a freshly
  // mapped/filtered array each render (new ref, same contents). Keying on identity would
  // thrash the highlight back to -1 every render; keying on the id sequence resets only
  // when the visible rows actually change. Newline separator avoids concatenation aliasing.
  const idsKey = items.map(getItemId).join('\n');
  useEffect(() => { setActiveIndex(-1); }, [idsKey]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0 || activeIndex >= items.length) return;
    const el = document.getElementById(getItemId(items[activeIndex]));
    el?.scrollIntoView?.({ block: 'nearest' });   // jsdom has no scrollIntoView
  }, [activeIndex, isOpen, items, getItemId]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!items.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i < 0 ? 0 : Math.min(i + 1, items.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i < 0 ? items.length - 1 : Math.max(i - 1, 0)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = activeIndex >= 0 ? items[activeIndex] : items[0];
      if (target) onCommit(target);
    }
  }, [items, activeIndex, onCommit]);

  const activeDescendantId = activeIndex >= 0 && activeIndex < items.length
    ? getItemId(items[activeIndex]) : undefined;

  return { activeIndex, activeDescendantId, onKeyDown };
}
